// Только тип: esbuild убирает `import type`, alias в тестах не разрешается.
import type { RegenerateErrorCode } from '@features/CardsWorkspace';

/**
 * Что показать после неудачного «Повторить · 1 жетон» у элемента записи (ТЗ главной § 22.6, § 23.5). Чистые
 * функции без React — их собирают тесты; побочные действия (квота, перечитка состава, проверка неизвестного
 * исхода) — в `useRetryItem`.
 */
export interface RetryFailure {
  /** Ключ строки под элементом (14 `danger`, `role="alert"`); `null` — строки нет. */
  key: string | null;
  /** Слова нет в словаре: вместо «Повторить» — «Добавить вручную», как у отказа модерации; фокус — на неё. */
  manual: boolean;
  /** Свой 429 жетонов при известной квоте: кнопка сразу «Жетоны закончились», строки нет, диктору — скрытая. */
  noCredits: boolean;
  /** Строка уходит, как только кнопка стала «Жетоны закончились» (квоты в кэше не было, § 22.6). */
  goneWhenOut: boolean;
}

const failure = (key: string | null, extra: Partial<RetryFailure> = {}): RetryFailure => ({
  key,
  manual: false,
  noCredits: false,
  goneWhenOut: false,
  ...extra,
});

/**
 * Ошибка «Повторить» → строка под элементом и замена кнопки. `quotaKnown` — квота уже в кэше: тогда после 429
 * жетонов кнопка сама говорит «Жетоны закончились», и второй строкой то же не пишем (правило владельца «одно и
 * то же дважды не показывать»). NETWORK и SERVER сюда приходят после перечитки состава, когда запуск не прошёл.
 */
export function retryFailure(code: RegenerateErrorCode, quotaKnown: boolean): RetryFailure {
  switch (code) {
    case 'DAILY_LIMIT_REACHED':
      return quotaKnown ? failure(null, { noCredits: true }) : failure('generations.action.noCredits', { goneWhenOut: true });
    case 'RATE_LIMIT':
      return failure('cardsWorkspace.errors.rateLimit');
    // Словарный гейт до резерва (§ 23.2): жетон не списан, повтор заведомо упрётся в то же — выход вручную.
    case 'NOT_IN_DICTIONARY':
      return failure('cardsWorkspace.errors.regenNotInDictionary', { manual: true });
    case 'CARD_NOT_FOUND':
    case 'CARD_STATE_NOT_FOUND':
      return failure('generations.errors.cardGone');
    // Статус уже другой — без текста, состав перечитывается.
    case 'CARD_NOT_REGENERATABLE':
      return failure(null);
    default:
      return failure('generations.errors.retryFailed');
  }
}

/** Ключ видимой строки под элементом: «Жетоны закончились» без квоты в кэше уходит, когда кнопка стала такой же. */
export function retryFailureLine(value: RetryFailure | null, buttonOut: boolean): string | null {
  if (!value?.key) return null;
  return value.goneWhenOut && buttonOut ? null : value.key;
}

/** «Добавить вручную» есть, когда строка создания открыта (почта подтверждена) и у записи есть модуль (§ 22.6). */
export function canAddManually(ctx: { composerLocked: boolean }, moduleId: string | null | undefined): boolean {
  return !ctx.composerLocked && !!moduleId;
}
